import { useState } from 'react';
import { Badge, Panel, RuleId, ScreenHeader, StateBox, StatusBadge, Table, humanize, type Column, type CoreBadgeTone } from '@garnet/ui';
import { useAsync } from '../useAsync.js';
import type { AdminApi, RuleRelease } from '../data/types.js';
import { StatStrip, dateTimeLabel } from '../components/AdminBits.js';

const RELEASE_TONE: Record<RuleRelease['state'], CoreBadgeTone> = {
  live: 'b-pass',
  superseded: 'b-neutral',
  rolled_back: 'b-fail',
};

type RuleRow = RuleRelease['rules'][number];

/**
 * Rules manager (PRD §20). Published, versioned classification/crosswalk rule sets; every
 * record records the version it used. Select a release to inspect the rule versions frozen
 * into it (Core PRD §15 release snapshots).
 */
export function Rules({ api }: { api: AdminApi }) {
  const { data, loading, error } = useAsync(() => api.getRuleReleases());
  const [selected, setSelected] = useState<string | null>(null);

  if (loading) return <StateBox kind="loading" title="Loading rule releases" />;
  if (error || !data) return <StateBox kind="error" title="Could not load rule releases">{error}</StateBox>;
  if (data.length === 0) return <StateBox kind="empty" title="No rule releases published" />;

  const current = data.find((r) => r.id === (selected ?? data.find((x) => x.state === 'live')?.id)) ?? data[0]!;

  const releaseColumns: Column<RuleRelease>[] = [
    { key: 'name', header: 'Release', mono: true, render: (r) => r.name },
    { key: 'state', header: 'State', render: (r) => <Badge tone={RELEASE_TONE[r.state]}>{humanize(r.state)}</Badge> },
    { key: 'rules', header: 'Rules', mono: true, render: (r) => String(r.rules.length) },
    { key: 'signed', header: 'Signed', mono: true, render: (r) => (r.signed ? '✓' : '—') },
    { key: 'cut', header: 'Cut', mono: true, render: (r) => `${dateTimeLabel(r.cut_at)} · ${r.cut_by}` },
  ];

  const ruleColumns: Column<RuleRow>[] = [
    { key: 'id', header: 'Rule', render: (r) => <RuleId id={r.rule_identifier} /> },
    { key: 'title', header: 'Title', render: (r) => r.title },
    { key: 'domain', header: 'Domain', mono: true, render: (r) => humanize(r.domain) },
    { key: 'juris', header: 'Juris', mono: true, render: (r) => r.jurisdiction.toUpperCase() },
    { key: 'version', header: 'Ver', mono: true, render: (r) => `v${r.version}` },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <div className="screen">
      <ScreenHeader eyebrow="Rules manager" title="Published rule-set versions" lead="Versioned classification and crosswalk rule sets; every record names the version it used. Select a release to inspect the rule versions frozen into it." />
      <StatStrip
        stats={[
          { label: 'Releases', value: data.length },
          { label: 'Live', value: data.filter((r) => r.state === 'live').length, tone: 'pass' },
          { label: 'Rolled back', value: data.filter((r) => r.state === 'rolled_back').length, tone: 'fail' },
          { label: 'Signed', value: data.filter((r) => r.signed).length, tone: 'accent' },
        ]}
      />
      <Panel title="Published rule-set versions" right={<span className="metatext">select a release</span>} flush>
        <Table
          columns={releaseColumns}
          rows={data}
          rowKey={(r) => r.id}
          selectedKey={current.id}
          onRowClick={(r) => setSelected(r.id)}
        />
      </Panel>
      <Panel
        title={`Rules in ${current.name}`}
        right={<span className="metatext">{current.changelog}</span>}
        flush
        bodyStyle={{ maxHeight: 480, overflow: 'auto' }}
      >
        <Table columns={ruleColumns} rows={[...current.rules]} rowKey={(r) => `${r.rule_identifier}@${r.version}`} />
      </Panel>
    </div>
  );
}
